import { AffiliateSourceId, AffiliateSourceLabel } from '../data-types/affiliates/affiliatesEnums';
import { AffiliateData } from '../data-types/affiliates/affiliatesInterfaces';
import { CookieType } from '../data-types/cookies/cookiesEnums';
import { cookies } from '../initialisation/initialisation';
import { store } from '../state/store';

export const BTAG_COOKIE_DAYS_TO_LIVE = 30;

export const generateRegistrationIframeUrl = (userId: number) => {
  const { betkwiffDomain } = store.getState().customization.featureFlags;
  const mid = betkwiffDomain ? '5' : '2';

  return `https://wlkwiff.adsrv.eacdn.com/Processing/Pixels/Registration.ashx?Playerid=${userId}&mid=${mid}`;
};

export const generateSalesIframeUrl = (userId: number) => {
  const {
    customization: {
      featureFlags: { betkwiffDomain },
    },
    wallet: {
      depositTrackingInfo: { transactionId, depositAmount },
    },
  } = store.getState();

  const mid = betkwiffDomain ? '5' : '2';

  return `https://wlkwiff.adsrv.eacdn.com/Processing/Pixels/Sales.ashx?Playerid=${userId}&mid=${mid}&saleid=${transactionId}&dpst=${depositAmount}`;
};

export const computeExpirationDate = (days: number) => {
  const todayDate = new Date().getDate();
  const futureDate = new Date().setDate(todayDate + days);
  return new Date(futureDate);
};

export const getCookie = (tag: string) => {
  return cookies.get(tag) || undefined;
};

export const setCookie = (tag: string, value?: string, cookieExpirationDate?: Date) => {
  cookies.set(tag, value, { path: '/', expires: cookieExpirationDate });
};

export const handleUserBTagUsage = (bTagValue: string) => {
  const existingBTag = getCookie(CookieType.BTAG);

  if (bTagValue && bTagValue !== existingBTag) {
    setCookie(CookieType.BTAG, bTagValue, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const handleAffIdUsage = (affId: string) => {
  const existingAffId = getCookie(CookieType.AFF_ID);

  if (affId && affId !== existingAffId) {
    setCookie(CookieType.AFF_ID, affId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const handleSourceIdUsage = (sourceId: string) => {
  const existingSourceId = getCookie(CookieType.SOURCE_ID);

  if (sourceId && sourceId !== existingSourceId) {
    setCookie(CookieType.SOURCE_ID, sourceId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const handleAIdUsage = (aId: string) => {
  const existingAId = getCookie(CookieType.A_ID);

  if (aId && aId !== existingAId) {
    setCookie(CookieType.A_ID, aId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const handleBannerIdUsage = (bannerId: string) => {
  const existingBannerId = getCookie(CookieType.BANNER_ID);

  if (bannerId && bannerId !== existingBannerId) {
    setCookie(CookieType.BANNER_ID, bannerId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const handleSiteIdUsage = (siteId: string) => {
  const existingSiteId = getCookie(CookieType.SITE_ID);

  if (siteId && siteId !== existingSiteId) {
    setCookie(CookieType.SITE_ID, siteId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const setupIdsSeparatelyFromBTag = (bTag: string) => {
  const splitRegex = /[a-zA-Z]_/;
  const splittedBTag = bTag.split(splitRegex);
  const numberOfSearchParams = splittedBTag.length;

  if (numberOfSearchParams >= 2) {
    const siteId = splittedBTag[1];
    setCookie(CookieType.SITE_ID, siteId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }

  if (numberOfSearchParams >= 3) {
    const bannerId = splittedBTag[2];
    setCookie(CookieType.BANNER_ID, bannerId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }

  if (numberOfSearchParams >= 4) {
    const aId = splittedBTag[3];
    setCookie(CookieType.A_ID, aId, computeExpirationDate(BTAG_COOKIE_DAYS_TO_LIVE));
  }
};

export const getAffiliatePayload = (): AffiliateData | undefined => {
  const bTag: string | undefined = getCookie(CookieType.BTAG);
  const affId: string | undefined = getCookie(CookieType.AFF_ID);
  const sourceId: string | undefined = getCookie(CookieType.SOURCE_ID);
  const aid: string | undefined = getCookie(CookieType.A_ID);
  const bannerId: string | undefined = getCookie(CookieType.BANNER_ID);
  const siteId: string | undefined = getCookie(CookieType.SITE_ID);

  let payload: AffiliateData = {
    attributionSource: AffiliateSourceId.INCOME_ACCESS,
    referenceId: undefined,
    sourceId: undefined,
    affId: undefined,
    siteId: undefined,
    aid: undefined,
    bannerId: undefined,
  };

  let addedAtLeastOneInfo = false;

  if (bTag) {
    payload = { ...payload, referenceId: String(bTag) };
    addedAtLeastOneInfo = true;
  }

  if (affId) {
    payload = { ...payload, affId: String(affId) };
    addedAtLeastOneInfo = true;
  }

  if (sourceId) {
    payload = { ...payload, sourceId: String(sourceId) };
    addedAtLeastOneInfo = true;
  }

  if (aid) {
    payload = { ...payload, aid: String(aid) };
    addedAtLeastOneInfo = true;
  }

  if (bannerId) {
    payload = { ...payload, bannerId: String(bannerId) };
    addedAtLeastOneInfo = true;
  }

  if (siteId) {
    payload = { ...payload, siteId: String(siteId) };
    addedAtLeastOneInfo = true;
  }

  if (addedAtLeastOneInfo) {
    return payload;
  }

  return undefined;
};

export const getSourceIdByName = (source: string) => {
  switch (source.toLowerCase()) {
    case AffiliateSourceLabel.INCOME_ACCESS.toLowerCase():
      return AffiliateSourceId.INCOME_ACCESS;
    case AffiliateSourceLabel.ADJUST.toLowerCase():
      return AffiliateSourceId.ADJUST;
    default:
      return source;
  }
};
